"use client";

import {
  type ReactNode,
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { ThemeProvider, useTheme } from "next-themes";
import { Toaster } from "sonner";
import { preten } from "@/styles/fonts";
import FocusTimerDialog from "./project_components/timer";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import GlobalDialog from "./project_components/globalDialog";
import { useDialogStore } from "@/lib/store/globalDialogStore";

export const AppContext = createContext<{
  isFocusTimerOpen: boolean;
  toggleFocusTimer: () => void;
}>({
  isFocusTimerOpen: false,
  toggleFocusTimer: () => {},
});

const ToasterProvider = () => {
  const { theme } = useTheme() as {
    theme: "light" | "dark" | "system";
  };

  // Avoid rendering Toaster until after mount to prevent setState during render warnings
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return (
    <Toaster
      duration={2000}
      richColors
      position="bottom-left"
      expand={true}
      theme={theme}
      toastOptions={{
        className: `${preten.className} mt-10 text-sm`,
      }}
    />
  );
};

export default function Providers({ children }: { children: ReactNode }) {
  const isOpen = useDialogStore((state) => state.open);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  const [isFocusTimerOpen, setIsFocusTimerOpen] = useState(false);

  const toggleFocusTimer = useCallback(() => {
    setIsFocusTimerOpen((prev) => !prev);
  }, []);

  const appContextValue = useMemo(
    () => ({
      isFocusTimerOpen,
      toggleFocusTimer,
    }),
    [isFocusTimerOpen, toggleFocusTimer],
  );

  return (
    <ThemeProvider
      attribute="class"
      enableSystem
      disableTransitionOnChange
      defaultTheme="system"
    >
      <QueryClientProvider client={queryClient}>
        <AppContext.Provider value={appContextValue}>
          {children}
          <FocusTimerDialog isOpen={isFocusTimerOpen} />
          {isOpen && <GlobalDialog />}
          {/* Place Toaster after app content to avoid setState during render warnings */}
          <ToasterProvider />
        </AppContext.Provider>
        {/* {process.env.NODE_ENV === "development" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )} */}
      </QueryClientProvider>
    </ThemeProvider>
  );
}
